import {
  Alignment,
  Button,
  ButtonGroup,
  Callout,
  Checkbox,
  Dialog,
  Icon,
  InputGroup,
  Intent,
  Popover,
  PopoverInteractionKind,
  Position,
  Spinner
} from '@blueprintjs/core'
import { isDarkTheme } from '@blueprintjs/core/lib/esm/common/utils'
import { is } from 'bluebird'
import { BotConfig } from 'botpress/sdk'
import { confirmDialog, lang, telemetry, toast } from 'botpress/shared'
import cx from 'classnames'
import _ from 'lodash'
import React, { Component, Fragment } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { generatePath, RouteComponentProps } from 'react-router'
import api from '~/app/api'
import { Downloader } from '~/app/common/Downloader'
import LoadingSection from '~/app/common/LoadingSection'
import PageContainer from '~/app/common/PageContainer'
import SplitPage from '~/app/common/SplitPage'
import { AppState } from '~/app/rootReducer'
import AccessControl from '~/auth/AccessControl'
import { getActiveWorkspace } from '~/auth/basicAuth'
import { fetchLicensing } from '~/management/licensing/reducer'
import { fetchModules } from '~/management/modules/reducer'
import { fetchBotHealth, fetchBots, fetchBotNLULanguages } from '~/workspace/bots/reducer'
import { filterList } from '~/workspace/util'
import packageJson from '../../../../../package.json'
import { encryptPayload } from '../../../aes-encryption'
import { formDataStore, jwtKeyStore, subDataStore } from '../../../token-store'
import bgImage from '../../assets/images/bg.png'
import BotItemCompact from './BotItemCompact'
import BotItemPipeline from './BotItemPipeline'
import { BotsLimitExceededDialog } from './BotsLimitExceededDialog/BotsLimitExceededDialog'
import CreateBotModal from './CreateBot-screens/CreateBotModal'
import EditStageModal from './EditStageModal'
import ImportBotModal from './ImportBotModal'
import RollbackBotModal from './RollbackBotModal'
import style from './style.scss'
import { SubscriptionSuggestionDialog } from './SubscriptionSuggestionDialog/SubscriptionSuggestionDialog'

const CURRENT_VERSION = packageJson.version
const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'

const botFilterFields = ['name', 'id', 'description']

type Props = ConnectedProps<typeof connector> & RouteComponentProps

class Bots extends Component<Props> {
  formData = formDataStore.get() || {}
  subData = subDataStore.get() || {}
  token = jwtKeyStore.get() || ''
  state = {
    isCreateBotModalOpen: false,
    isRollbackModalOpen: false,
    isImportBotModalOpen: false,
    isEditStageModalOpen: false,
    focusedBot: null,
    selectedStage: null,
    archiveUrl: undefined,
    archiveName: '',
    filter: '',
    showFilters: false,
    needApprovalFilter: false,
    isExpired: false,
    showBotsExceedDialog: false,
    showExpiryPrompt: false,
    expiryMessage: '',
    numberOfBots: this.formData.numberOfBots || 0,
    isLoading: false, // existing loader state
    showSuggestionsDialog: false,
    upgradeInProgress: false, // new state for upgrade loader
    selectedBotIds: [] as string[], // new state for selected bot ids in the Bots limit exceeded Dialog
  }

  // New handler to call the /pro-suggestion-update API with proper loader and error handling
  handleUpgradeNow = async (from: 'upgrade' | 'current', selectedDuration?: 'monthly' | 'yearly') => {
    this.setState({ upgradeInProgress: true })
    const duration = selectedDuration || 'monthly'
    const plan = from === 'upgrade' ? 'Professional' : 'Starter'

    try {
      const response = await fetch(`${API_URL}/pro-suggestion-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({
          payload: encryptPayload({
            plan,
            duration,
            isDowngrade: false // Default to false
          })
        })
      })

      if (!response.ok) {
        toast.failure(lang.tr('Failed to save suggestion'))
        return
      }

      const res = await response.json()

      if (!res.success) {
        toast.failure(lang.tr('Failed to save suggestion'))
        return
      }

      const priceNum = duration === 'monthly' ? 45 : 432
      const nextSubs = plan === 'Starter'
        ? { ...this.formData.nextSubs, suggested: true }
        : { plan, duration, price: `$${priceNum}`, isDowngrade: false, suggested: true }

      this.formData = { ...this.formData, nextSubs }
      formDataStore.set(this.formData)

      toast.success(lang.tr('Suggestion saved successfully'))
    } catch (err) {
      console.error(err)
      toast.failure(lang.tr('Failed to save suggestion'))
    } finally {
      this.setState({ showSuggestionsDialog: false, showBotsExceedDialog: false, upgradeInProgress: false })
    }
  }

  // New handler to update checkbox selection for each bot
  handleCheckboxChange = (botId: string) => {
    this.setState(prevState => {
      const selectedBotIds = this.state.selectedBotIds.includes(botId)
        ? this.state.selectedBotIds.filter(id => id !== botId)
        : [...this.state.selectedBotIds, botId]
      return { selectedBotIds }
    })
  }

  // Revised handler for confirming selected bots
  handleConfirmSelectedBots = async () => {
    const botIds = [...this.state.selectedBotIds]
    if (!botIds.length) {
      return
    }

    this.setState({ isLoading: true, showBotsExceedDialog: false, selectedBotIds: [] })

    const successBots: string[] = []
    const failedBots: string[] = []

    for (const botId of botIds) {
      try {
        await this.deleteBot(botId, false) // Disable confirmation for batch deletion
        successBots.push(botId)
      } catch (err) {
        failedBots.push(botId)
      }
    }

    this.setState({ isLoading: false })

    if (successBots.length) {
      toast.success(`${successBots.length} bot(s) deleted successfully.`)
    }
    if (failedBots.length) {
      toast.failure(`Failed to delete: ${failedBots.join(', ')}`)
    }
    this.props.fetchBots()
  }

  async componentDidMount() {
    this.props.fetchBots()
    this.props.fetchBotHealth()
    this.props.fetchBotNLULanguages()

    if (!this.props.loadedModules.length && this.props.profile && this.props.profile.isSuperAdmin) {
      this.props.fetchModules()
    }

    if (!this.props.licensing) {
      this.props.fetchLicensing()
    }

    setTimeout(() => {
      this.formData = formDataStore.get() || {}
      this.subData = subDataStore.get() || {}

      // Auto-select bots with 'twilio' channel key
      if (this.formData.filteredBots && this.formData.filteredBots.length) {
        const autoSelected = this.formData.filteredBots
          .filter((bot: any) => bot.messaging?.channels && Object.keys(bot.messaging.channels)[0] === 'twilio')
          .map((bot: any) => bot.id)
        this.setState({ selectedBotIds: autoSelected })
      }

      // Check subscription expiry
      let expiry
      const daysRemaining = this.subData.daysRemaining || 0
      const subscription = this.subData.subscription || 'Trial'
      const promptRun = this.subData.promptRun || false

      if (subscription === 'Trial' || subscription === 'Demo') {
        expiry = this.subData.expired || false
      } else {
        expiry = this.subData.expired && daysRemaining <= -4
      }

      this.setState({
        numberOfBots: this.formData.numberOfBots || 0,
        isExpired: expiry
      })

      if (subscription === 'Starter' && !expiry && (this.formData.numberOfBots || 0) > 3) {
        this.setState({ showBotsExceedDialog: true })
      }

      // check for prompt run status and trigger it accordingly
      if (!promptRun && subscription !== 'Demo') {
        let msg
        if ((daysRemaining === 15 && subscription === 'Trial') || (daysRemaining < 0 && subscription === 'Trial')) {
          return
        }

        // This dialog should be shown only for Trial(non - cancelled) users before 1 to 3 days remaining for expiry
        if (daysRemaining <= 3 && daysRemaining > 0 &&
          subscription === 'Trial' &&
          this.formData.nextSubs && this.formData.nextSubs.plan === 'Starter' &&
          this.formData.nextSubs && !this.formData.nextSubs.suggested &&
          (this.formData.numberOfBots || 0) > 3 &&
          this.subData.isCancelled === false) {
          this.setState({ showSuggestionsDialog: true })
        }


        if (daysRemaining === 15 || daysRemaining === 7 || daysRemaining === 5 || daysRemaining === 3 || daysRemaining === 1) {

          if (this.subData.isCancelled === true) {
            msg = `You have ${daysRemaining} days left for expiry. You won't be able to use the services after that. Please visit the Subscription page to renew a plan.`
          } else {
            msg = `You have ${daysRemaining} days left for expiry. We will be renewing your subscription automatically. If you want to cancel the subscription, please visit the Subscription page.`
          }
        }

        if (daysRemaining === -1 || daysRemaining === -2 || daysRemaining === -3) {
          msg = `Currently your subscription plan has been expired. But ${(daysRemaining === -1) ? 'we have decided to give you' : 'you still have'} ${Math.abs(daysRemaining + 4)} complimentary day(s) to renew your subscription.`
        }

        if (msg) {
          this.setState({
            showExpiryPrompt: true,
            expiryMessage: msg
          })

          subDataStore.set({ ... this.subData, promptRun: true })  // set prompt run to true

          // Hide the prompt after 8 seconds
          setTimeout(() => {
            this.setState({ showExpiryPrompt: false })
          }, 8000)
        }

      }
    }, 700) // Delay to ensure symbol data is populated

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    telemetry.startFallback(api.getSecured({ useV1: true })).catch()
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.numberOfBots !== this.props.numberOfBots) {
      this.setState({ numberOfBots: this.props.numberOfBots })
    }
  }

  toggleCreateBotModal = () => {
    this.setState({ isCreateBotModalOpen: !this.state.isCreateBotModalOpen })
  }

  toggleImportBotModal = () => {
    this.setState({ isImportBotModalOpen: !this.state.isImportBotModalOpen })
  }

  async exportBot(botId: string) {
    this.setState({
      archiveUrl: `/admin/workspace/bots/${botId}/export`,
      archiveName: `bot_${botId}_${Date.now()}.tgz`
    })
  }

  async deleteBot(botId: string, enableCheck = true) {
    const savedFormData = formDataStore.get() || {}

    if (enableCheck) {
      const confirmed = await confirmDialog(lang.tr('admin.workspace.bots.confirmDelete'), {
        acceptLabel: lang.tr('delete')
      })
      if (!confirmed) {
        return
      }
    }

    this.setState({ isLoading: true }) // Show the loader
    try {
      await api.getSecured().post(`/admin/workspace/bots/${savedFormData.fullName}/${savedFormData.email}/${botId}/delete`)
      this.props.fetchBots()

      // Log bot deletion activity
      void this.botDeletionActivityLog(
        savedFormData.email,
        savedFormData.fullName,
        botId,
        'SUCCESS',
        `Bot deleted successfully with ID: ${botId}`
      )

      setTimeout(() => {
        window.location.reload()    // reloading for the bot creation limit check
      }, 500)
      toast.success(lang.tr('The bot has been deleted successfully'))
    } catch (err) {
      // Log bot deletion activity
      void this.botDeletionActivityLog(
        savedFormData.email,
        savedFormData.fullName,
        botId,
        'FAILED',
        `Bot deletion failed with ID: ${botId}, reason: ${err.message || 'An unexpected error occurred.'}`
      )
      console.error(err)
      toast.failure(lang.tr('The bot could not be deleted'))
    } finally {
      this.setState({ isLoading: false }) // Hide the loader
    }
  }

  // Log activity
  botDeletionActivityLog = async (email, fullName, botId, status, context) => {
    const token = jwtKeyStore.get() || ''

    await fetch(`${API_URL}/bot-deletion-log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ payload: encryptPayload({ fullName, botId, status, context }) }),
    })
  }

  async reloadBot(botId: string) {
    try {
      await api.getSecured().post(`/admin/workspace/bots/${botId}/reload`)
      this.props.fetchBots()
      this.props.fetchBotHealth()
      toast.success(lang.tr('admin.workspace.bots.remounted'))
    } catch (err) {
      console.error(err)
      toast.failure(lang.tr('admin.workspace.bots.couldNotMount'))
    }
  }

  viewLogs(botId: string) {
    this.props.history.push(
      generatePath('/workspace/:workspaceId?/logs?botId=:botId', {
        workspaceId: getActiveWorkspace() || undefined,
        botId
      })
    )
  }

  renderCreateNewBotButton() {
    const { subscription } = this.subData
    const { numberOfBots } = this.state

    // Determine the bot limit based on the subscription type (for starter, it's 3 and for demo or professional, it's 5)
    const botLimit = subscription === 'Starter' ? 3 : 5

    // Check if the number of bots exceeds the limit
    if (numberOfBots >= botLimit) {
      return null // Do not render the button if the limit is reached
    }

    return (
      <AccessControl resource="admin.bots.*" operation="write">
        <Popover
          minimal={false}
          interactionKind={PopoverInteractionKind.CLICK}
          position={Position.BOTTOM}
          content={
            <div
              style={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                minWidth: '220px',
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            >
              <Button
                id="btn-new-bot"
                text={lang.tr('admin.workspace.bots.new')}
                icon="add"
                intent={Intent.PRIMARY}
                style={{
                  height: '45px',
                  borderRadius: '6px'
                }}
                onClick={() => this.setState({ isCreateBotModalOpen: true })}
              />
              <Button
                id="btn-import-bot"
                text={lang.tr('admin.workspace.bots.importExisting')}
                icon="import"
                intent={Intent.SUCCESS}
                style={{
                  height: '45px',
                  borderRadius: '6px'
                }}
                onClick={() => this.setState({ isImportBotModalOpen: true })}
              />
            </div>
          }
        >
          <Button
            id="btn-create-bot"
            intent={Intent.NONE}
            text={lang.tr('admin.workspace.bots.createBot')}
            rightIcon="caret-down"
            style={{
              height: '45px',
              background: '#f0f5ff',
              border: '1px solid #c3d4ff',
              borderRadius: '6px',
              padding: '8px 16px',
              fontWeight: 600,
              fontSize: '15px',
              color: '#304ffe'
            }}
          />
        </Popover>
      </AccessControl>
    )
  }

  hasUnlangedBots = () => {
    return this.props.bots.reduce((hasUnlangedBots, bot) => hasUnlangedBots || !bot.defaultLanguage, false)
  }

  async requestStageChange(botId: string) {
    await api.getSecured({ timeout: 60000 }).post(`/admin/workspace/bots/${botId}/stage`)
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.promoted'))
  }

  async approveStageChange(botId) {
    await api.getSecured({ timeout: 60000 }).post(`/admin/workspace/bots/${botId}/approve-stage`)
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.approvedPromotion'))
  }

  isLicensed = () => {
    return _.get(this.props.licensing, 'status') === 'licensed'
  }

  async createRevision(botId) {
    await api.getSecured().post(`admin/workspace/bots/${botId}/revisions`)
    toast.success(lang.tr('admin.workspace.bots.revisionsCreated'))
  }

  toggleRollbackModal = (botId?: string) => {
    this.setState({
      focusedBot: typeof botId === 'string' ? botId : null,
      isRollbackModalOpen: !this.state.isRollbackModalOpen
    })
  }

  handleRollbackSuccess = () => {
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.rollbackSuccess'))
  }

  handleEditStageSuccess = () => {
    this.props.fetchBots()
  }

  toggleEditStage = (stage?) => {
    this.setState({
      selectedStage: stage ? stage : null,
      isEditStageModalOpen: !this.state.isEditStageModalOpen
    })
  }

  toggleFilters = () => {
    this.setState({ showFilters: !this.state.showFilters })
  }

  findBotError(botId: string) {
    if (!this.props.health) {
      return false
    }

    return _.some(
      this.props.health.map(x => x.bots[botId]),
      s => s && s.status === 'unhealthy'
    )
  }

  renderCompactView(bots: BotConfig[]) {
    if (!bots.length) {
      return null
    }

    return (
      <div className={cx(style.bot_views, style.compact_view, style.table)}>
        {bots.map(bot => (
          <Fragment key={bot.id}>
            <BotItemCompact
              bot={bot}
              loadedModules={this.props.loadedModules}
              hasError={this.findBotError(bot.id)}
              deleteBot={this.deleteBot.bind(this, bot.id)}
              exportBot={this.exportBot.bind(this, bot.id)}
              // createRevision={this.createRevision.bind(this, bot.id)}
              // rollback={this.toggleRollbackModal.bind(this, bot.id)}
              reloadBot={this.reloadBot.bind(this, bot.id)}
              // viewLogs={this.viewLogs.bind(this, bot.id)}
              installedNLULanguages={this.props.language}
            />
          </Fragment>
        ))}
      </div>
    )
  }

  renderPipelineView(bots: BotConfig[]) {
    const { pipeline } = this.props.workspace || {}
    const { email, strategy } = this.props.profile || {}

    const botsByStage = _.groupBy(bots, 'pipeline_status.current_stage.id')
    const colSize = Math.floor(12 / pipeline.length)

    return (
      <Fragment>
        <div className={cx(style.row, style.pipeline_view, style.bot_views)}>
          {pipeline.map((stage, idx) => {
            const allowStageChange = this.isLicensed() && idx !== pipeline.length - 1
            return (
              <div key={stage.id} style={{ flex: colSize, marginRight: 20 }}>
                {pipeline.length > 1 && (
                  <div className={style.pipeline_title}>
                    <p>{stage.label}</p>
                    <AccessControl resource="admin.bots.*" operation="write" superAdmin>
                      <Button className={style.pipeline_edit_button} onClick={() => this.toggleEditStage(stage)}>
                        <Icon icon="edit" />
                      </Button>
                    </AccessControl>
                  </div>
                )}
                {idx === 0 && (
                  <div className={cx(style.pipeline_bot, style.create)}>{this.renderCreateNewBotButton()}</div>
                )}
                {(botsByStage[stage.id] || []).map(bot => (
                  <Fragment key={bot.id}>
                    <BotItemPipeline
                      loadedModules={this.props.loadedModules}
                      bot={bot}
                      isApprover={stage.reviewers.find(r => r.email === email && r.strategy === strategy) !== undefined}
                      userEmail={email!}
                      userStrategy={strategy!}
                      hasError={this.findBotError(bot.id)}
                      allowStageChange={allowStageChange && !bot.disabled}
                      requestStageChange={this.requestStageChange.bind(this, bot.id)}
                      approveStageChange={this.approveStageChange.bind(this, bot.id)}
                      deleteBot={this.deleteBot.bind(this, bot.id)}
                      exportBot={this.exportBot.bind(this, bot.id)}
                      createRevision={this.createRevision.bind(this, bot.id)}
                      rollback={this.toggleRollbackModal.bind(this, bot.id)}
                      reloadBot={this.reloadBot.bind(this, bot.id)}
                      viewLogs={this.viewLogs.bind(this, bot.id)}
                      installedNLULanguages={this.props.language}
                    />
                  </Fragment>
                ))}
              </div>
            )
          })}
        </div>
      </Fragment>
    )
  }

  filterStageApproval(bot: BotConfig, email: string, strategy: string) {
    if (!this.props.workspace || !this.state.needApprovalFilter) {
      return true
    }
    const { pipeline } = this.props.workspace
    const { current_stage, stage_request } = bot.pipeline_status

    const reviewers = _.get(current_stage && pipeline.find(x => x.id === current_stage.id), 'reviewers', [])
    const isReviewer = reviewers.find(x => x.strategy === strategy && x.email === email)

    return stage_request && isReviewer
  }

  renderBots() {
    const { email, strategy } = this.props.profile || {}
    const { filter, showFilters, needApprovalFilter } = this.state

    const filteredBots = filterList<BotConfig>(this.props.bots, botFilterFields, this.state.filter).filter(x =>
      this.filterStageApproval(x, email!, strategy!)
    )

    const hasBots = !!this.props.bots.length
    const botsView = this.isPipelineView ?
      this.renderPipelineView(filteredBots) :
      this.renderCompactView(filteredBots)

    return (
      <div className={style.container} style={{
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #d0d0d0',
        borderRadius: '8px',
        backgroundColor: 'white',
        marginTop: '20px'
      }}>
        {/* Header with title and stats */}
        <div className={style.header}>
          <div className={style.titleSection}>
            <h1 className={style.title}>{'Your Bots'}</h1>
            {hasBots && (
              <span className={style.botCount}>
                {filteredBots.length} of {(this.subData.subscription === 'Starter') ? '3' : '5'} bots
              </span>
            )}
          </div>

          {/* Create new bot button */}
          {this.renderCreateNewBotButton()}
        </div>

        {/* Main content area */}
        <div className={style.content}>
          {hasBots ? (
            <>
              {/* Search and filter section */}
              <div className={style.searchFilterSection}>
                <div className={style.searchContainer}>
                  <InputGroup
                    leftIcon="search"
                    id="input-filter"
                    placeholder={lang.tr('admin.workspace.bots.filter')}
                    value={filter}
                    onChange={e => this.setState({ filter: e.target.value.toLowerCase() })}
                    autoComplete="off"
                    className={style.searchField}
                  />

                  {this.isPipelineView && (
                    <Button
                      icon="filter"
                      onClick={this.toggleFilters}
                      minimal={true}
                      active={showFilters}
                      className={style.filterToggle}
                    >
                      {lang.tr('admin.workspace.bots.filters')}
                    </Button>
                  )}
                </div>

                {/* View toggle */}
                {/* <div className={style.viewToggle}>
                  <ButtonGroup>
                    <Button
                      icon="grid-view"
                      active={!this.isPipelineView}
                      onClick={() => this.setState({ isPipelineView: false })}
                      minimal={true}
                    />
                    <Button
                      icon="diagram-tree"
                      active={this.isPipelineView}
                      onClick={() => this.setState({ isPipelineView: true })}
                      minimal={true}
                    />
                  </ButtonGroup>
                </div> */}
              </div>

              {/* Extra filters panel */}
              {showFilters && (
                <div className={style.extraFiltersPanel}>
                  <div className={style.extraFiltersHeader}>
                    <h4>{lang.tr('admin.workspace.bots.extraFilters')}</h4>
                    <Button
                      icon="cross"
                      minimal={true}
                      small={true}
                      onClick={this.toggleFilters}
                    />
                  </div>
                  <div className={style.extraFiltersContent}>
                    <Checkbox
                      label={lang.tr('admin.workspace.bots.needYourApproval')}
                      checked={needApprovalFilter}
                      onChange={e => this.setState({ needApprovalFilter: e.currentTarget.checked })}
                    />
                    {/* Additional filters could be added here */}
                  </div>
                </div>
              )}

              {/* No results message */}
              {filter && !filteredBots.length && (
                <Callout
                  title={lang.tr('admin.workspace.bots.noBotMatches')}
                  icon="search"
                  intent={Intent.WARNING}
                  className={style.noResultsCallout}
                >
                </Callout>
              )}
            </>
          ) : (
            <>
              {/* Empty state */}
              <div className={style.emptyState}>
                <div className={style.emptyStateIllustration}>
                  <Icon icon="cube" iconSize={64} />
                </div>
                <Callout
                  title={lang.tr('admin.workspace.bots.noBotYet')}
                  className={style.emptyStateCallout}
                >
                  <p>{lang.tr('admin.workspace.bots.alwaysAssignedToWorkspace')}</p>
                  <p>{lang.tr('admin.workspace.bots.createYourFirstBot')}</p>
                </Callout>
              </div>
            </>
          )}

          {/* Warning for bots without language */}
          {this.hasUnlangedBots() && (
            <Callout
              intent={Intent.WARNING}
              icon="translate"
              className={style.warningCallout}
            >
              {lang.tr('admin.workspace.bots.noSpecifiedLanguage')}
            </Callout>
          )}

          {/* Bots list/pipeline view */}
          <div className={style.botsContainer}>
            {botsView}
          </div>
        </div>
      </div >
    )
  }

  get isPipelineView() {
    return this.props.workspace && this.props.workspace.pipeline && this.props.workspace.pipeline.length > 1
  }

  render() {
    const { showExpiryPrompt, expiryMessage, isLoading, showSuggestionsDialog, showBotsExceedDialog, upgradeInProgress, selectedBotIds } = this.state

    if (!this.props.bots) {
      return <LoadingSection />
    }

    // Check if this is a demo account
    const isDemoAccount = this.subData.subscription === 'Demo'

    return (
      <div style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        height: '100%',
        width: '100%',
        overflow: 'hidden'
      }}>
        <PageContainer title={lang.tr('admin.workspace.bots.bots')} contentClassName="transparent-bg">
          {/* Robot Banner Overlay */}
          <div style={{
            position: 'absolute',
            top: '72%',
            left: '71%',
            transform: 'translateY(-50%)',
            width: 'auto',
            height: 'auto',
            maxWidth: '700px',
            maxHeight: '700px',
            pointerEvents: 'none',
            zIndex: 0,
          }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/sub-banner.png`}
              alt="Robot Banner"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: 0.8,
              }}
            />
          </div>

          {/* Demo Account Indicator */}
          {isDemoAccount && (
            <div
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 10001,
                backgroundColor: '#ff9800',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                letterSpacing: '0.5px'
              }}
            >
              This is a limited DEMO Account
            </div>
          )}

          {/* Full-screen loader for other actions */}
          {isLoading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.37)',
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Spinner intent="primary" size={50} />
            </div>
          )}

          {/* Full-screen loader for upgrade suggestion */}
          {upgradeInProgress && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '1.5em',
                color: '#106ba3'
              }}
            >
              <Spinner intent="primary" size={50} />
              <div style={{ marginTop: '20px', fontWeight: 600 }}>Suggestion getting saved... Please don't close this tab</div>
            </div>
          )}

          {/* Expiry Prompt */}
          {showExpiryPrompt && (
            <div className={style.expiryPrompt}>
              {expiryMessage}
            </div>
          )}

          {/* Subscription suggestion Dialog UI */}
          <SubscriptionSuggestionDialog
            isOpen={showSuggestionsDialog}
            numberOfBots={this.formData.numberOfBots || 4}
            nextSubsPlan={this.formData.nextSubs?.plan}
            nextSubsDuration={this.formData.nextSubs?.duration}
            upgradeInProgress={upgradeInProgress}
            onConfirm={(duration) => this.handleUpgradeNow('upgrade', duration)}
            onKeepCurrent={() => this.handleUpgradeNow('current')}
          />

          {/* Bots limit exceeded UI */}
          <BotsLimitExceededDialog
            isOpen={showBotsExceedDialog}
            filteredBots={this.formData.filteredBots || []}
            selectedBotIds={this.state.selectedBotIds || []}
            maxBotsAllowed={3}
            onCheckboxChange={this.handleCheckboxChange}
            onConfirmSelectedBots={this.handleConfirmSelectedBots}
          />

          <SplitPage sideMenu={(this.state.isExpired) ? !this.isPipelineView : !this.isPipelineView}>
            <Fragment>
              {this.state.isExpired && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '400px',
                    padding: '20px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    backgroundColor: 'white'
                  }}
                >
                  <h3>
                    Your {(this.subData.subscription === 'Trial') ? 'Trial' : this.subData.subscription} subscription {(this.subData.subscription !== 'Trial' && this.subData.subscription !== 'Demo') ? 'and a 3 day complimentary trial' : ''} has been Expired
                  </h3>
                  <p style={{ margin: '20px 0' }}>
                    To continue using the platform, please {this.subData.subscription === 'Demo' ? 'contact with xMati team support' : 'purchase a subscription'}.
                  </p>
                </div>
              )}

              {/* Render the rest of the content if not expired */}
              {!this.state.isExpired && (
                <>
                  <Downloader url={this.state.archiveUrl} filename={this.state.archiveName} />
                  {this.renderBots()}
                  <AccessControl resource="admin.bots.*" operation="write">
                    <RollbackBotModal
                      botId={this.state.focusedBot}
                      isOpen={this.state.isRollbackModalOpen}
                      toggle={this.toggleRollbackModal}
                      onRollbackSuccess={this.handleRollbackSuccess}
                    />
                    <EditStageModal
                      workspace={this.props.workspace}
                      stage={this.state.selectedStage}
                      isOpen={this.state.isEditStageModalOpen}
                      toggle={this.toggleEditStage}
                      onEditSuccess={this.handleEditStageSuccess}
                    />
                    <CreateBotModal
                      isOpen={this.state.isCreateBotModalOpen}
                      toggle={this.toggleCreateBotModal}
                      existingBots={this.props.bots}
                      onCreateBotSuccess={this.props.fetchBots}
                    />
                    <ImportBotModal
                      isOpen={this.state.isImportBotModalOpen}
                      toggle={this.toggleImportBotModal}
                      onCreateBotSuccess={this.props.fetchBots}
                    />
                  </AccessControl>
                </>
              )}
            </Fragment>
          </SplitPage>
        </PageContainer>
      </div>
    )
  }
}

const mapStateToProps = (state: AppState) => ({
  loadedModules: state.modules.loadedModules,
  bots: state.bots.bots,
  health: state.bots.health,
  workspace: state.bots.workspace,
  loading: state.bots.loadingBots,
  licensing: state.licensing.license,
  profile: state.user.profile,
  language: state.bots.nluLanguages,
  numberOfBots: state.bots.numberOfBots, // Add numberOfBots
})

const mapDispatchToProps = {
  fetchBots,
  fetchLicensing,
  fetchBotHealth,
  fetchModules,
  fetchBotNLULanguages
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(Bots)
